import { memo, useContext, useEffect, useRef, useState } from 'react';
import { Button, ProgressBar, Spinner, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { webSocketUrl } from '@/app/common/api/config';
import { Centrifuge } from 'centrifuge';
import { useGenerateTokenMutation } from '@/app/common/api/socketApi';
import analysisApi from '@/app/PageSpeed/api/analysisApi';
import { RemoveAnalysisContext } from '@/context/Context';
import IconAlert from '@/components/common/IconAlert';
import { isAnalysisInProgress } from '../utils/analysisStatus';

type ProgressData = {
  total?: number;
  finished?: number;
  status?: number;
  isDone?: boolean;
  task_statuses?: TaskProgress[];
  [key: string]: unknown;
};

type TaskProgress = {
  task_id: string;
  isDone?: boolean;
};

type Props = {
  job_id: string;
  url?: string;
  onComplete?: () => void;
  onFinished?: () => void;
};

function normalizeProgressData(raw: unknown): ProgressData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const outer = raw as ProgressData & { data?: ProgressData };
  if (
    outer.total !== undefined ||
    outer.finished !== undefined ||
    outer.status !== undefined ||
    outer.isDone !== undefined
  ) {
    return outer;
  }
  if (outer.data && typeof outer.data === 'object') {
    return outer.data;
  }
  return undefined;
}

function isProgressFinished(data: ProgressData | undefined): boolean {
  if (!data) return false;
  if (data.isDone === true) return true;
  if (
    data.status !== undefined &&
    data.status !== null &&
    !isAnalysisInProgress(data.status)
  ) {
    return true;
  }
  if (
    data.total !== undefined &&
    data.finished !== undefined &&
    data.total === data.finished &&
    data.total > 0
  ) {
    return true;
  }
  return false;
}

function isProgressSuccessful(data: ProgressData | undefined): boolean {
  return data?.status === 6;
}

const AnalysisProgressBar = ({ job_id, url, onComplete, onFinished }: Props) => {
  const dispatch = useDispatch();
  const { setShow, setStatus, setAnalysisUrl, setAnalysisId } = useContext(
    RemoveAnalysisContext
  );
  const [token, setToken] = useState<string | null>(null);
  const [progressData, setProgressData] = useState<ProgressData | undefined>();
  const [connected, setConnected] = useState(false);
  const [generateToken] = useGenerateTokenMutation();
  const hasCompletedRef = useRef(false);
  const refetchedTaskIdsRef = useRef<Set<string>>(new Set());
  const completeTimeoutRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const progressBarPercent = progressData
    ? Math.floor(
        (100 / (progressData?.total || 1)) * (progressData?.finished || 0)
      )
    : 0;

  useEffect(() => {
    hasCompletedRef.current = false;
    refetchedTaskIdsRef.current.clear();
    setToken(null);
    setProgressData(undefined);
    setConnected(false);
    if (completeTimeoutRef.current) {
      window.clearTimeout(completeTimeoutRef.current);
      completeTimeoutRef.current = null;
    }

    const fetchToken = async () => {
      try {
        const response = await generateToken({
          id: job_id,
          channels: [`personal:#${job_id}`]
        }).unwrap();
        setToken(response.data ?? null);
      } catch (err: unknown) {
        console.error('Error fetching data:', err);
      }
    };

    fetchToken();
  }, [generateToken, job_id]);

  useEffect(() => {
    if (!token) return;

    const centrifugo = new Centrifuge(webSocketUrl, {
      getToken: async () => token
    });

    centrifugo.on('connected', () => {
      setConnected(true);
    });

    centrifugo.on('disconnected', () => {
      setConnected(false);
    });

    centrifugo.on('publication', (message: { data?: unknown }) => {
      const data = normalizeProgressData(message?.data);
      if (!data) return;

      setProgressData(data);

      const completedTaskIds = (data.task_statuses || [])
        .filter(
          (task): task is TaskProgress =>
            Boolean(task?.task_id) && task.isDone === true
        )
        .map(task => task.task_id)
        .filter(taskId => !refetchedTaskIdsRef.current.has(taskId));

      if (completedTaskIds.length) {
        completedTaskIds.forEach(taskId =>
          refetchedTaskIdsRef.current.add(taskId)
        );
        dispatch(analysisApi.util.invalidateTags(['AnalysisHistory']));
      }

      if (isProgressFinished(data) && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        centrifugo.disconnect();
        dispatch(analysisApi.util.invalidateTags(['AnalysisHistory']));
        if (isProgressSuccessful(data)) {
          completeTimeoutRef.current = window.setTimeout(() => {
            onCompleteRef.current?.();
            onFinishedRef.current?.();
          }, 2500);
        } else {
          onFinishedRef.current?.();
        }
      }
    });

    centrifugo.connect();

    return () => {
      if (completeTimeoutRef.current) {
        window.clearTimeout(completeTimeoutRef.current);
        completeTimeoutRef.current = null;
      }
      centrifugo.disconnect();
    };
  }, [token, dispatch]);

  if (isProgressFinished(progressData)) {
    return (
      <IconAlert
        variant={isProgressSuccessful(progressData) ? 'success' : 'danger'}
        dismissible
      >
        <p className="mb-0 fs--1">
          {isProgressSuccessful(progressData)
            ? 'بررسی با موفقیت انجام شد.'
            : 'بررسی با خطا پایان یافت. نتیجه در جدول تاریخچه قابل مشاهده است.'}
        </p>
      </IconAlert>
    );
  }

  return (
    <Row className="my-3">
      <Col md={12}>
        <div className="d-flex flex-column flex-md-row align-items-stretch">
          <div className="flex-1">
            <div className="d-flex justify-content-between rounded bg-body-tertiary shadow-sm p-2 gap-1 align-items-center">
              <Spinner
                style={{
                  width: '20px',
                  marginBottom: '-3px',
                  paddingLeft: '3px',
                  height: '20px'
                }}
                className="me-2"
              />
              {progressData ? (
                <div className="w-100">
                  {progressData?.status === 2 &&
                    'درصف دریافت لطفا منتظر بمانید ... '}
                  {progressData?.status === 4 && (
                    <>
                      <div className="mb-1" style={{ fontSize: '14px' }}>
                        در حال واکاوی
                        <span className="float-end">
                          {Math.floor(
                            (100 / (progressData?.total || 1)) *
                              (progressData?.finished || 0)
                          )}
                          %
                        </span>
                      </div>
                      <ProgressBar
                        now={progressBarPercent}
                        style={{ height: 5 }}
                        className="rounded-pill align-middle"
                        variant="success"
                      />
                    </>
                  )}
                  {progressData?.status !== 2 && progressData?.status !== 4 && (
                    <span className="flex-1">
                      در حال دریافت وضعیت واکاوی ...
                    </span>
                  )}
                </div>
              ) : (
                <span className="flex-1">
                  {connected
                    ? 'در حال اتصال برای دریافت اطلاعات واکاوی ...'
                    : 'در حال برقراری ارتباط سوکت ...'}
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => {
              setAnalysisId(job_id);
              setAnalysisUrl(url || '');
              setShow(true);
              setStatus(2);
            }}
            className="ms-2 fs--1"
            variant="danger"
          >
            توقف واکاوی
          </Button>
        </div>
      </Col>
    </Row>
  );
};

export default memo(AnalysisProgressBar);
